import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[role='button']:not([aria-disabled='true'])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

let wasLastInteractionKeyboard = false;

if (typeof document !== "undefined") {
  document.addEventListener(
    "keydown",
    () => {
      wasLastInteractionKeyboard = true;
    },
    true
  );
  document.addEventListener(
    "pointerdown",
    () => {
      wasLastInteractionKeyboard = false;
    },
    true
  );
}

export const useRouteFocus = (containerRef: React.RefObject<HTMLElement>): void => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!wasLastInteractionKeyboard) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    let observer: MutationObserver | undefined;
    let isFocusAssigned = false;

    const assignFocus = (): boolean => {
      if (isFocusAssigned) {
        return true;
      }

      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement?.closest('[role="dialog"]')) {
        isFocusAssigned = true;
        return true;
      }

      const focusTarget = container.querySelector<HTMLElement>(focusableSelector);
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
        isFocusAssigned = true;
        return true;
      }

      const isLoading = container.querySelector('[aria-busy="true"]');
      const heading = container.querySelector<HTMLElement>("h1");
      if (!isLoading && heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
        isFocusAssigned = true;
        return true;
      }

      return false;
    };

    const focusTimer = window.setTimeout(() => {
      if (assignFocus()) {
        return;
      }

      // Async pages replace their loading state after the route effect has already run.
      observer = new MutationObserver(() => {
        if (assignFocus()) {
          observer?.disconnect();
        }
      });
      observer.observe(container, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ["aria-busy", "disabled", "hidden"],
      });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      observer?.disconnect();
    };
  }, [containerRef, location.pathname]);
};
